import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Avatar,
  Badge,
  Paper,
  SimpleGrid,
  Progress,
  Tooltip,
} from '@mantine/core';
import {
  IconUsers,
  IconMail,
  IconCalendar,
} from '@tabler/icons-react';
import { Card, Button } from '../../ui';
import { FadeIn, HoverLift, StaggeredList } from '../../animations';
import { PersonData } from './PersonCard';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface TeamData {
  id: string;
  name: string;
  description?: string;
  avatar?: string;
  lead?: PersonData;
  members: PersonData[];
  department?: string;
  tags?: string[];
  stats?: {
    projects?: number;
    tasks?: number;
    completion?: number;
  };
  metadata?: Record<string, unknown>;
}

export interface TeamListProps {
  teams: TeamData[];
  variant?: 'cards' | 'list' | 'compact';
  onTeamClick?: (team: TeamData) => void;
  onMemberClick?: (member: PersonData, team: TeamData) => void;
  showMembers?: boolean;
  maxMembersShown?: number;
  showStats?: boolean;
  columns?: 1 | 2 | 3 | 4;
  className?: string;
}

export const TeamList = forwardRef<HTMLDivElement, TeamListProps>(
  (
    {
      teams,
      variant = 'cards',
      onTeamClick,
      onMemberClick,
      showMembers = true,
      maxMembersShown = 5,
      showStats = true,
      columns = 3,
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const renderTeamCard = (team: TeamData) => (
      <HoverLift lift={4}>
        <Card
          style={{ cursor: onTeamClick ? 'pointer' : 'default', height: '100%' }}
          onClick={() => onTeamClick?.(team)}
        >
          <Stack gap="md">
            {/* Header */}
            <Group justify="space-between">
              <Group gap="sm">
                <Avatar src={team.avatar} size="lg" radius="md" color="blue">
                  {team.name.charAt(0)}
                </Avatar>
                <Box>
                  <Text fw={600} style={accessibilityStyles}>{team.name}</Text>
                  {team.department && (
                    <Badge size="xs" variant="light">{team.department}</Badge>
                  )}
                </Box>
              </Group>
              <Group gap={4}>
                <IconUsers size={14} color="gray" />
                <Text size="sm" c="dimmed" style={accessibilityStyles}>{team.members.length}</Text>
              </Group>
            </Group>

            {/* Description */}
            {team.description && (
              <Text size="sm" c="dimmed" lineClamp={2} style={accessibilityStyles}>
                {team.description}
              </Text>
            )}

            {/* Lead */}
            {team.lead && (
              <Paper p="xs" bg="gray.0" radius="sm">
                <Group gap="sm">
                  <Avatar src={team.lead.avatar} size="sm" radius="xl">
                    {team.lead.firstName?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>Team Lead</Text>
                    <Text size="sm" fw={500} style={accessibilityStyles}>
                      {team.lead.firstName} {team.lead.lastName}
                    </Text>
                  </Box>
                </Group>
              </Paper>
            )}

            {/* Members */}
            {showMembers && team.members.length > 0 && (
              <Box>
                <Text size="xs" c="dimmed" mb="xs" style={accessibilityStyles}>Members</Text>
                <Avatar.Group>
                  {team.members.slice(0, maxMembersShown).map((member) => (
                    <Tooltip key={member.id} label={`${member.firstName} ${member.lastName}`}>
                      <Avatar
                        src={member.avatar}
                        size="sm"
                        radius="xl"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMemberClick?.(member, team);
                        }}
                      >
                        {member.firstName?.charAt(0)}
                      </Avatar>
                    </Tooltip>
                  ))}
                  {team.members.length > maxMembersShown && (
                    <Avatar size="sm" radius="xl">
                      +{team.members.length - maxMembersShown}
                    </Avatar>
                  )}
                </Avatar.Group>
              </Box>
            )}

            {/* Stats */}
            {showStats && team.stats && (
              <Box>
                {team.stats.completion !== undefined && (
                  <Box>
                    <Group justify="space-between" mb={4}>
                      <Text size="xs" c="dimmed" style={accessibilityStyles}>Project Progress</Text>
                      <Text size="xs" fw={500} style={accessibilityStyles}>{team.stats.completion}%</Text>
                    </Group>
                    <Progress value={team.stats.completion} size="sm" />
                  </Box>
                )}
                <Group gap="lg" mt="xs">
                  {team.stats.projects !== undefined && (
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>
                      {team.stats.projects} Projects
                    </Text>
                  )}
                  {team.stats.tasks !== undefined && (
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>
                      {team.stats.tasks} Tasks
                    </Text>
                  )}
                </Group>
              </Box>
            )}

            {/* Tags */}
            {team.tags && team.tags.length > 0 && (
              <Group gap="xs">
                {team.tags.slice(0, 3).map((tag) => (
                  <Badge key={tag} size="xs" variant="outline">
                    {tag}
                  </Badge>
                ))}
              </Group>
            )}
          </Stack>
        </Card>
      </HoverLift>
    );

    const renderTeamListItem = (team: TeamData) => (
      <HoverLift lift={2}>
        <Paper
          p="md"
          withBorder
          style={{ cursor: onTeamClick ? 'pointer' : 'default' }}
          onClick={() => onTeamClick?.(team)}
        >
          <Group justify="space-between">
            <Group gap="md">
              <Avatar src={team.avatar} size="md" radius="md" color="blue">
                {team.name.charAt(0)}
              </Avatar>
              <Box>
                <Group gap="xs">
                  <Text fw={500} style={accessibilityStyles}>{team.name}</Text>
                  {team.department && (
                    <Badge size="xs" variant="light">{team.department}</Badge>
                  )}
                </Group>
                {team.description && (
                  <Text size="sm" c="dimmed" lineClamp={1} style={accessibilityStyles}>{team.description}</Text>
                )}
              </Box>
            </Group>
            <Group gap="xl">
              {showMembers && (
                <Avatar.Group>
                  {team.members.slice(0, 3).map((member) => (
                    <Avatar key={member.id} src={member.avatar} size="sm" radius="xl">
                      {member.firstName?.charAt(0)}
                    </Avatar>
                  ))}
                  {team.members.length > 3 && (
                    <Avatar size="sm" radius="xl">+{team.members.length - 3}</Avatar>
                  )}
                </Avatar.Group>
              )}
              {showStats && team.stats?.completion !== undefined && (
                <Box w={100}>
                  <Progress value={team.stats.completion} size="sm" />
                </Box>
              )}
            </Group>
          </Group>
        </Paper>
      </HoverLift>
    );

    const renderCompactItem = (team: TeamData) => (
      <Paper
        p="sm"
        withBorder
        style={{ cursor: onTeamClick ? 'pointer' : 'default' }}
        onClick={() => onTeamClick?.(team)}
      >
        <Group justify="space-between">
          <Group gap="sm">
            <Avatar src={team.avatar} size="sm" radius="md" color="blue">
              {team.name.charAt(0)}
            </Avatar>
            <Text size="sm" fw={500} style={accessibilityStyles}>{team.name}</Text>
          </Group>
          <Group gap="xs">
            <IconUsers size={12} color="gray" />
            <Text size="xs" c="dimmed" style={accessibilityStyles}>{team.members.length}</Text>
          </Group>
        </Group>
      </Paper>
    );

    return (
      <FadeIn>
        <Box ref={ref} className={className}>
          {variant === 'cards' && (
            <SimpleGrid cols={{ base: 1, sm: 2, md: Math.min(columns, 3), lg: columns }}>
              <StaggeredList staggerDelay={50}>
                {teams.map((team) => (
                  <Box key={team.id}>{renderTeamCard(team)}</Box>
                ))}
              </StaggeredList>
            </SimpleGrid>
          )}

          {variant === 'list' && (
            <Stack gap="sm">
              <StaggeredList staggerDelay={30}>
                {teams.map((team) => (
                  <Box key={team.id}>{renderTeamListItem(team)}</Box>
                ))}
              </StaggeredList>
            </Stack>
          )}

          {variant === 'compact' && (
            <Stack gap="xs">
              {teams.map((team) => (
                <Box key={team.id}>{renderCompactItem(team)}</Box>
              ))}
            </Stack>
          )}
        </Box>
      </FadeIn>
    );
  }
);

TeamList.displayName = 'TeamList';

export default TeamList;
